import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import CustomCalendar from '../components/CustomCalendar';
import CustomTimePicker from '../components/CustomTimePicker';
import { TransactionHistory } from '../components/TransactionHistory';
import { 
  lookupBooking, 
  customerEditBooking, 
  customerCancelBooking, 
  createPaymentCheckout,
  getPaymentStatus 
} from '../services/api';
import { 
  Loader2, CheckCircle, XCircle, Clock, CreditCard, Edit3, 
  Trash2, AlertTriangle, Mail, Calendar, MapPin, Car, Users,
  Phone, Plane, Baby, MessageSquare, RefreshCw, ArrowLeft, ArrowLeftRight,
  ChevronDown
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Status badge component - moved outside to prevent re-renders
const StatusBadge = ({ status, type }) => {
  const styles = {
    booking: {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    },
    payment: {
      unpaid: 'bg-red-100 text-red-800',
      partial: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      refunded: 'bg-gray-100 text-gray-800',
    }
  };
  
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[type]?.[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const BookingManagementPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  
  // State
  const [email, setEmail] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verified, setVerified] = useState(false);
  
  // Payment state
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [showEditErrors, setShowEditErrors] = useState(false);
  
  // Cancel state
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  
  // Ref to prevent duplicate cancelled email
  const cancelledNotificationSent = useRef(false);
  
  // Transaction state
  const [transactions, setTransactions] = useState([]);

  // Dropdown states for edit mode
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showReturnDatePicker, setShowReturnDatePicker] = useState(false);
  const [showReturnTimePicker, setShowReturnTimePicker] = useState(false);
  const [showPassengersDropdown, setShowPassengersDropdown] = useState(false);
  const [showBabyDropdown, setShowBabyDropdown] = useState(false);
  const [showChildDropdown, setShowChildDropdown] = useState(false);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = () => {
      setShowDatePicker(false);
      setShowTimePicker(false);
      setShowReturnDatePicker(false);
      setShowReturnTimePicker(false);
      setShowPassengersDropdown(false);
      setShowBabyDropdown(false);
      setShowChildDropdown(false);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Format helpers
  const formatDateDisplay = (date) => {
    if (!date) return 'Select date';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch {
      return date;
    }
  };

  const formatTimeDisplay = (time) => {
    if (!time) return 'Select time';
    try {
      const [hours, minutes] = time.split(':');
      const h = parseInt(hours);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    } catch {
      return time;
    }
  };

  // Fetch transactions when verified
  const fetchTransactions = async () => {
    if (!verified || !email || !bookingId) return;
    try {
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}/transactions/customer?email=${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    }
  };

  useEffect(() => {
    if (verified && email && bookingId) {
      fetchTransactions();
    }
  }, [verified, email, bookingId]);

  // Check for payment status on mount
  useEffect(() => {
    const paymentParam = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentParam === 'success' && sessionId) {
      setPaymentSuccess(true);
      // Check payment status and update booking
      checkPaymentStatus(sessionId);
    } else if (paymentParam === 'cancelled' && !cancelledNotificationSent.current) {
      cancelledNotificationSent.current = true;
      setPaymentCancelled(true);
      // Notify backend to send cancelled payment email
      notifyPaymentCancelled();
    }
  }, [searchParams, bookingId]);

  const notifyPaymentCancelled = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/cancelled/${bookingId}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Failed to notify payment cancelled:', err);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      await getPaymentStatus(sessionId);
      
      // Get email from state or sessionStorage
      const storedEmail = email || sessionStorage.getItem(`booking_${bookingId}_email`);
      
      if (storedEmail) {
        // Refresh booking data after payment
        const data = await lookupBooking(bookingId, storedEmail);
        setBooking(data);
        setEmail(storedEmail);
        setVerified(true);
        
        // Also refresh transactions
        try {
          const txnRes = await fetch(`${API_BASE}/api/bookings/${bookingId}/transactions/customer?email=${encodeURIComponent(storedEmail)}`);
          if (txnRes.ok) {
            const txnData = await txnRes.json();
            setTransactions(txnData);
          }
        } catch (txnErr) {
          console.error('Failed to fetch transactions:', txnErr);
        }
      }
    } catch (err) {
      console.error('Payment status check error:', err);
    }
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const data = await lookupBooking(bookingId, email);
      setBooking(data);
      setVerified(true);
      // Store email in sessionStorage for convenience
      sessionStorage.setItem(`booking_${bookingId}_email`, email);
    } catch (err) {
      setError(err.message || 'Booking not found or email doesn\'t match');
    } finally {
      setLoading(false);
    }
  };

  // Try to auto-verify from sessionStorage
  useEffect(() => {
    const storedEmail = sessionStorage.getItem(`booking_${bookingId}_email`);
    if (storedEmail) {
      setEmail(storedEmail);
      // Auto-verify after a short delay
      setTimeout(() => {
        lookupBooking(bookingId, storedEmail)
          .then(data => {
            setBooking(data);
            setVerified(true);
          })
          .catch(() => {
            // Silent fail, user will need to verify manually
          });
      }, 100);
    }
  }, [bookingId]);

  const handlePayment = async (paymentFor = 'full') => {
    setPaymentProcessing(true);
    try {
      const response = await createPaymentCheckout(bookingId, paymentFor);
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (err) {
      setError(err.message || 'Failed to start payment');
      setPaymentProcessing(false);
    }
  };

  // Validate edit form and return errors
  const validateEditForm = () => {
    const newErrors = {};
    const pickupDate = editData.pickup_date || booking?.pickup_date;
    const pickupTime = editData.pickup_time || booking?.pickup_time;
    const name = editData.name ?? booking?.name;
    const phone = editData.phone ?? booking?.phone;
    const flightNumber = editData.flight_number ?? booking?.flight_number;
    const pickupAddress = editData.pickup_address ?? booking?.pickup_address;
    const dropoffAddress = editData.dropoff_address ?? booking?.dropoff_address;
    
    // Date & Time validation
    if (!pickupDate) newErrors.pickup_date = 'Please select a date';
    if (!pickupTime) newErrors.pickup_time = 'Please select a time';
    
    // Contact validation
    if (!name?.trim()) newErrors.name = 'Please enter your full name';
    if (!phone?.trim()) newErrors.phone = 'Please enter your phone number';
    
    // Conditional field validation
    if (isFromAirport && !flightNumber?.trim()) {
      newErrors.flight_number = 'Please enter your flight number';
    }
    if (!isFromAirport && !pickupAddress?.trim()) {
      newErrors.pickup_address = 'Please enter your pickup address';
    }
    if (!isToAirport && !dropoffAddress?.trim()) {
      newErrors.dropoff_address = 'Please enter your drop-off address';
    }
    
    // Return trip validation
    if (booking?.has_return_trip) {
      const returnDate = editData.return_date || booking?.return_date;
      const returnTime = editData.return_time || booking?.return_time;
      const returnFlightNumber = editData.return_flight_number ?? booking?.return_flight_number;
      
      if (!returnDate) {
        newErrors.return_date = 'Please select a return date';
      } else if (pickupDate && new Date(returnDate) <= new Date(pickupDate)) {
        newErrors.return_date = 'Return date must be after pickup date';
      }
      
      if (!returnTime) newErrors.return_time = 'Please select a return time';
      
      if (returnIsFromAirport && !returnFlightNumber?.trim()) {
        newErrors.return_flight_number = 'Please enter your return flight number';
      }
    }
    
    setEditErrors(newErrors);
    return newErrors;
  };

  // Scroll to first error field
  const scrollToFirstError = (errorKeys) => {
    const fieldOrder = [
      'pickup_date', 'pickup_time', 'flight_number', 'pickup_address', 'dropoff_address',
      'return_date', 'return_time', 'return_flight_number',
      'name', 'phone'
    ];
    
    for (const field of fieldOrder) {
      if (errorKeys.includes(field)) {
        const element = document.querySelector(`[data-field="${field}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      }
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError(null);
    setShowEditErrors(true);
    
    const errors = validateEditForm();
    const errorKeys = Object.keys(errors);
    
    if (errorKeys.length > 0) {
      // Scroll to first error after state updates
      setTimeout(() => scrollToFirstError(errorKeys), 100);
      return;
    }
    
    setEditLoading(true);
    
    try {
      const updatedBooking = await customerEditBooking(bookingId, email, editData);
      setBooking(updatedBooking);
      setEditMode(false);
      setEditData({});
      setEditErrors({});
      setShowEditErrors(false);
    } catch (err) {
      setEditError(err.message || 'Failed to update booking');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true);
    try {
      const cancelledBooking = await customerCancelBooking(bookingId, email);
      setBooking(cancelledBooking);
      setShowCancelConfirm(false);
    } catch (err) {
      setError(err.message || 'Failed to cancel booking');
    } finally {
      setCancelLoading(false);
    }
  };

  const refreshBooking = async () => {
    setLoading(true);
    try {
      const data = await lookupBooking(bookingId, email);
      setBooking(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check if within 24 hours
  const isWithin24Hours = () => {
    if (!booking) return false;
    try {
      const pickup = new Date(`${booking.pickup_date}T${booking.pickup_time}`);
      const now = new Date();
      const diff = pickup - now;
      return diff < 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  };

  const canModify = booking && booking.booking_status !== 'cancelled' && !isWithin24Hours();
  const remainingBalance = booking ? booking.total_price - (booking.amount_paid || 0) : 0;
  const needsPayment = booking && booking.payment_status !== 'paid' && booking.booking_status !== 'cancelled';

  // Determine route type for conditional fields
  const isFromAirport = booking?.from_slug?.includes('airport') || false;
  const isToAirport = booking?.to_slug?.includes('airport') || false;
  // Return trip starts from where outbound ended (so if outbound is to airport, return is from airport)
  const returnIsFromAirport = isToAirport;

  // Verification form
  if (!verified) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header with dark background for verification page */}
        <div className="bg-[#2d232e]">
          <Header />
          <div className="h-24"></div> {/* Spacer for absolute header */}
        </div>
        <main className="py-16">
          <div className="max-w-md mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#36827f]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-[#36827f]" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Review Your Booking</h1>
                <p className="text-gray-600 mt-2">
                  Booking Reference: <strong className="text-[#36827f]">{bookingId?.toUpperCase()}</strong>
                </p>
              </div>
              
              {paymentSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <p className="text-green-700">Payment successful! Please enter your email to review your booking.</p>
                </div>
              )}

              {paymentCancelled && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                  <div>
                    <p className="text-amber-700 font-medium">Payment not completed</p>
                    <p className="text-amber-600 text-sm">Enter your email to access your booking and retry payment.</p>
                  </div>
                </div>
              )}
              
              <form onSubmit={handleVerify}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter the email address used for this booking
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 mb-4"
                />
                
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-red-700 text-sm">
                    {error}
                  </div>
                )}
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#36827f] hover:bg-[#2d6f6c] text-white py-3 rounded-xl"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'View My Booking'
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center">
                <Link to="/" className="text-[#36827f] hover:text-[#2d6f6c] text-sm">
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Main booking management view
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Header */}
      <section className="relative bg-[#2d232e] pt-28 pb-8">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="max-w-4xl mx-auto px-4 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm mb-4">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Booking <span className="text-[#36827f]">#{booking?.booking_id}</span></h1>
              <p className="text-white/70">Manage your reservation</p>
            </div>
            <Button onClick={refreshBooking} variant="outline" size="sm" disabled={loading} className="border-white/20 text-white hover:bg-white/10">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </section>
      
      <main className="py-8 -mt-4">
        <div className="max-w-4xl mx-auto px-4">

          {/* Payment Success Alert */}
          {paymentSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-800">Payment Successful!</p>
                <p className="text-green-700 text-sm">Your payment has been processed. A confirmation email has been sent.</p>
              </div>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Booking Status</p>
              <StatusBadge status={booking?.booking_status} type="booking" />
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Payment Status</p>
              <StatusBadge status={booking?.payment_status} type="payment" />
              {booking?.payment_status === 'partial' && (
                <p className="text-xs text-gray-500 mt-1">€{booking.amount_paid} paid of €{booking.total_price}</p>
              )}
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Booking Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Outbound Transfer Details */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-[#36827f]" />
                  </div>
                  Outbound Transfer
                </h2>
                
                <div className="space-y-4">
                  {/* Route Display */}
                  <div className="flex items-center gap-3 text-lg">
                    <span className="font-medium">{booking?.from_name}</span>
                    <span className="text-[#36827f]">→</span>
                    <span className="font-medium">{booking?.to_name}</span>
                  </div>
                  
                  {/* Date & Time Row */}
                  {editMode ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Date Picker */}
                      <div className="relative" data-field="pickup_date" onClick={(e) => e.stopPropagation()}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="h-4 w-4 inline mr-1 text-[#36827f]" /> Date
                        </label>
                        <button
                          type="button"
                          onClick={() => { setShowDatePicker(!showDatePicker); setShowTimePicker(false); if (editErrors.pickup_date) setEditErrors(prev => ({...prev, pickup_date: null})); }}
                          className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 hover:border-[#36827f] transition ${showEditErrors && editErrors.pickup_date ? 'border-red-500' : 'border-gray-200'}`}
                        >
                          <span className={!(editData.pickup_date || booking?.pickup_date) ? 'text-gray-400' : ''}>
                            {formatDateDisplay(editData.pickup_date || booking?.pickup_date)}
                          </span>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
                        </button>
                        {showEditErrors && editErrors.pickup_date && (
                          <p className="text-red-500 text-sm mt-1">{editErrors.pickup_date}</p>
                        )}
                        {showDatePicker && (
                          <div className="absolute top-full left-0 right-0 mt-2 z-30">
                            <CustomCalendar 
                              value={editData.pickup_date ? new Date(editData.pickup_date) : (booking?.pickup_date ? new Date(booking.pickup_date) : null)}
                              onChange={(date) => { 
                                // Use local date components to avoid timezone shift issues
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const formatted = `${year}-${month}-${day}`;
                                setEditData({...editData, pickup_date: formatted});
                                setEditErrors(prev => ({...prev, pickup_date: null}));
                              }}
                              onClose={() => setShowDatePicker(false)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Time Picker */}
                      <div className="relative" data-field="pickup_time" onClick={(e) => e.stopPropagation()}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Clock className="h-4 w-4 inline mr-1 text-[#36827f]" /> Time
                        </label>
                        <button
                          type="button"
                          onClick={() => { setShowTimePicker(!showTimePicker); setShowDatePicker(false); if (editErrors.pickup_time) setEditErrors(prev => ({...prev, pickup_time: null})); }}
                          className={`w-full flex items-center justify-between px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 hover:border-[#36827f] transition ${showEditErrors && editErrors.pickup_time ? 'border-red-500' : 'border-gray-200'}`}
                        >
                          <span className={!(editData.pickup_time || booking?.pickup_time) ? 'text-gray-400' : ''}>
                            {formatTimeDisplay(editData.pickup_time || booking?.pickup_time)}
                          </span>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showTimePicker ? 'rotate-180' : ''}`} />
                        </button>
                        {showEditErrors && editErrors.pickup_time && (
                          <p className="text-red-500 text-sm mt-1">{editErrors.pickup_time}</p>
                        )}
                        {showTimePicker && (
                          <div className="absolute top-full left-0 right-0 mt-2 z-30">
                            <CustomTimePicker 
                              value={editData.pickup_time || booking?.pickup_time}
                              onChange={(time) => { setEditData({...editData, pickup_time: time}); setEditErrors(prev => ({...prev, pickup_time: null})); }}
                              onClose={() => setShowTimePicker(false)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="h-4 w-4" /> Date
                        </p>
                        <p className="font-medium">{formatDateDisplay(booking?.pickup_date)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="h-4 w-4" /> Time
                        </p>
                        <p className="font-medium">{formatTimeDisplay(booking?.pickup_time)}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Flight Number - only show if FROM airport */}
                  {isFromAirport && (
                    <div data-field="flight_number">
                      <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                        <Plane className="h-4 w-4" /> Flight Number
                      </p>
                      {editMode ? (
                        <>
                          <input
                            value={editData.flight_number ?? booking?.flight_number ?? ''}
                            onChange={(e) => { setEditData({...editData, flight_number: e.target.value}); setEditErrors(prev => ({...prev, flight_number: null})); }}
                            placeholder="e.g. AB1234"
                            className={`w-full md:w-1/2 px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 hover:border-[#36827f] transition ${showEditErrors && editErrors.flight_number ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {showEditErrors && editErrors.flight_number && (
                            <p className="text-red-500 text-sm mt-1">{editErrors.flight_number}</p>
                          )}
                        </>
                      ) : (
                        <p className="font-medium">{booking?.flight_number || <span className="text-gray-400">Not provided</span>}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Pickup Address - only show if NOT from airport */}
                  {!isFromAirport && (
                    <div data-field="pickup_address">
                      <p className="text-sm text-gray-500 mb-1">Pickup Address</p>
                      {editMode ? (
                        <>
                          <input
                            value={editData.pickup_address ?? booking?.pickup_address ?? ''}
                            onChange={(e) => { setEditData({...editData, pickup_address: e.target.value}); setEditErrors(prev => ({...prev, pickup_address: null})); }}
                            placeholder="Hotel name, street address..."
                            className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 hover:border-[#36827f] transition ${showEditErrors && editErrors.pickup_address ? 'border-red-500' : 'border-gray-200'}`}
                        />
                          {showEditErrors && editErrors.pickup_address && (
                            <p className="text-red-500 text-sm mt-1">{editErrors.pickup_address}</p>
                          )}
                        </>
                      ) : (
                        <p className="font-medium">{booking?.pickup_address || <span className="text-gray-400">Not provided</span>}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Drop-off Address - only show if NOT to airport */}
                  {!isToAirport && (
                    <div data-field="dropoff_address">
                      <p className="text-sm text-gray-500 mb-1">Drop-off Address</p>
                      {editMode ? (
                        <>
                          <input
                            value={editData.dropoff_address ?? booking?.dropoff_address ?? ''}
                            onChange={(e) => { setEditData({...editData, dropoff_address: e.target.value}); setEditErrors(prev => ({...prev, dropoff_address: null})); }}
                            placeholder="Hotel name, street address..."
                            className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 hover:border-[#36827f] transition ${showEditErrors && editErrors.dropoff_address ? 'border-red-500' : 'border-gray-200'}`}
                          />
                          {showEditErrors && editErrors.dropoff_address && (
                            <p className="text-red-500 text-sm mt-1">{editErrors.dropoff_address}</p>
                          )}
                        </>
                      ) : (
                        <p className="font-medium">{booking?.dropoff_address || <span className="text-gray-400">Not provided</span>}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Return Trip - only show if booking has return trip */}
              {booking?.has_return_trip && (
                <div className="bg-green-50 rounded-2xl p-6 shadow-sm border border-green-200">
                  <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <ArrowLeftRight className="h-4 w-4 text-green-600" />
                    </div>
                    Return Trip Included
                  </h2>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-lg">
                      <span className="font-medium">{booking?.to_name}</span>
                      <span className="text-green-600">→</span>
                      <span className="font-medium">{booking?.from_name}</span>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      {/* Return Date */}
                      <div className="relative" data-field="return_date" onClick={(e) => e.stopPropagation()}>
                        <label className="block text-sm font-medium text-green-700 mb-2">
                          <Calendar className="h-4 w-4 inline mr-1" /> Return Date
                        </label>
                        {editMode ? (
                          <>
                            <button
                              type="button"
                              onClick={() => { setShowReturnDatePicker(!showReturnDatePicker); setShowReturnTimePicker(false); if (editErrors.return_date) setEditErrors(prev => ({...prev, return_date: null})); }}
                              className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-xl text-gray-900 hover:border-green-400 transition ${showEditErrors && editErrors.return_date ? 'border-red-500' : 'border-green-200'}`}
                            >
                              <span className={!(editData.return_date || booking?.return_date) ? 'text-gray-400' : ''}>
                                {formatDateDisplay(editData.return_date || booking?.return_date)}
                              </span>
                              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showReturnDatePicker ? 'rotate-180' : ''}`} />
                            </button>
                            {showEditErrors && editErrors.return_date && (
                              <p className="text-red-500 text-sm mt-1">{editErrors.return_date}</p>
                            )}
                            {showReturnDatePicker && (
                              <div className="absolute top-full left-0 right-0 mt-2 z-30">
                                <CustomCalendar 
                                  value={editData.return_date ? new Date(editData.return_date) : (booking?.return_date ? new Date(booking.return_date) : null)}
                                  onChange={(date) => { 
                                    // Use local date components to avoid timezone shift issues
                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const formatted = `${year}-${month}-${day}`;
                                    setEditData({...editData, return_date: formatted});
                                    setEditErrors(prev => ({...prev, return_date: null}));
                                  }}
                                  onClose={() => setShowReturnDatePicker(false)}
                                  minDate={editData.pickup_date || booking?.pickup_date}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="font-medium text-gray-900">{formatDateDisplay(booking?.return_date)}</p>
                        )}
                      </div>

                      {/* Return Time */}
                      <div className="relative" data-field="return_time" onClick={(e) => e.stopPropagation()}>
                        <label className="block text-sm font-medium text-green-700 mb-2">
                          <Clock className="h-4 w-4 inline mr-1" /> Return Time
                        </label>
                        {editMode ? (
                          <>
                            <button
                              type="button"
                              onClick={() => { setShowReturnTimePicker(!showReturnTimePicker); setShowReturnDatePicker(false); if (editErrors.return_time) setEditErrors(prev => ({...prev, return_time: null})); }}
                              className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-xl text-gray-900 hover:border-green-400 transition ${showEditErrors && editErrors.return_time ? 'border-red-500' : 'border-green-200'}`}
                            >
                              <span className={!(editData.return_time || booking?.return_time) ? 'text-gray-400' : ''}>
                                {formatTimeDisplay(editData.return_time || booking?.return_time)}
                              </span>
                              <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showReturnTimePicker ? 'rotate-180' : ''}`} />
                            </button>
                            {showEditErrors && editErrors.return_time && (
                              <p className="text-red-500 text-sm mt-1">{editErrors.return_time}</p>
                            )}
                            {showReturnTimePicker && (
                              <div className="absolute top-full left-0 right-0 mt-2 z-30">
                                <CustomTimePicker 
                                  value={editData.return_time || booking?.return_time}
                                  onChange={(time) => { setEditData({...editData, return_time: time}); setEditErrors(prev => ({...prev, return_time: null})); }}
                                  onClose={() => setShowReturnTimePicker(false)}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="font-medium text-gray-900">{formatTimeDisplay(booking?.return_time)}</p>
                        )}
                      </div>

                      {/* Return Flight Number - only show if return is FROM airport */}
                      {returnIsFromAirport && (
                        <div data-field="return_flight_number">
                          <p className="text-sm text-green-700 flex items-center gap-1 mb-1">
                            <Plane className="h-4 w-4" /> Return Flight
                          </p>
                          {editMode ? (
                            <>
                              <input
                                value={editData.return_flight_number ?? booking?.return_flight_number ?? ''}
                                onChange={(e) => { setEditData({...editData, return_flight_number: e.target.value}); setEditErrors(prev => ({...prev, return_flight_number: null})); }}
                                placeholder="e.g. AB1234"
                                className={`w-full px-4 py-3 bg-white border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 hover:border-green-400 transition ${showEditErrors && editErrors.return_flight_number ? 'border-red-500' : 'border-green-200'}`}
                              />
                              {showEditErrors && editErrors.return_flight_number && (
                                <p className="text-red-500 text-sm mt-1">{editErrors.return_flight_number}</p>
                              )}
                            </>
                          ) : (
                            <p className="font-medium text-gray-900">{booking?.return_flight_number || <span className="text-gray-400">Not provided</span>}</p>
                          )}
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-green-700">Return Trip Price</p>
                        <p className="font-medium text-green-600">+€{booking?.return_price || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Vehicle & Passengers */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                    <Car className="h-4 w-4 text-[#36827f]" />
                  </div>
                  Vehicle & Passengers
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Vehicle</p>
                    <p className="font-medium">{booking?.vehicle_name}</p>
                  </div>
                  
                  {/* Passengers Dropdown */}
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <p className="text-sm text-gray-500 mb-1">Passengers</p>
                    {editMode ? (
                      <>
                        <button
                          type="button"
                          onClick={() => { setShowPassengersDropdown(!showPassengersDropdown); setShowBabyDropdown(false); setShowChildDropdown(false); }}
                          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 hover:border-[#36827f] transition"
                        >
                          <span>{editData.passengers ?? booking?.passengers}</span>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showPassengersDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        {showPassengersDropdown && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                            {(() => {
                              const vehicleCapacities = { 'Standard Car': 3, 'Executive Car': 3, 'Standard Van': 7, 'Executive Van': 7 };
                              const maxCapacity = vehicleCapacities[booking?.vehicle_name] || 7;
                              return Array.from({length: maxCapacity}, (_, i) => i + 1).map(num => (
                                <div
                                  key={num}
                                  onClick={() => { setEditData({...editData, passengers: num}); setShowPassengersDropdown(false); }}
                                  className={`px-4 py-3 cursor-pointer transition-colors ${(editData.passengers ?? booking?.passengers) === num ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                  {num}
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="font-medium">{booking?.passengers}</p>
                    )}
                  </div>
                  
                  {/* Child Seats */}
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                      <Baby className="h-4 w-4" /> Child Seats
                    </p>
                    {editMode ? (
                      <div className="flex gap-4">
                        {/* Baby Seats Dropdown */}
                        <div className="flex-1 relative" onClick={(e) => e.stopPropagation()}>
                          <label className="text-xs text-gray-500 mb-1 block">Baby (0-1 yr)</label>
                          <button
                            type="button"
                            onClick={() => { setShowBabyDropdown(!showBabyDropdown); setShowChildDropdown(false); setShowPassengersDropdown(false); }}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 hover:border-[#36827f] transition"
                          >
                            <span>{editData.baby_seats ?? booking?.baby_seats ?? 0}</span>
                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBabyDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showBabyDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                              {[0, 1, 2, 3].map(num => (
                                <div
                                  key={num}
                                  onClick={() => { setEditData({...editData, baby_seats: num}); setShowBabyDropdown(false); }}
                                  className={`px-4 py-3 cursor-pointer transition-colors ${(editData.baby_seats ?? booking?.baby_seats ?? 0) === num ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                  {num}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Child Seats Dropdown */}
                        <div className="flex-1 relative" onClick={(e) => e.stopPropagation()}>
                          <label className="text-xs text-gray-500 mb-1 block">Child (2-5 yr)</label>
                          <button
                            type="button"
                            onClick={() => { setShowChildDropdown(!showChildDropdown); setShowBabyDropdown(false); setShowPassengersDropdown(false); }}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 hover:border-[#36827f] transition"
                          >
                            <span>{editData.child_seats ?? booking?.child_seats ?? 0}</span>
                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showChildDropdown ? 'rotate-180' : ''}`} />
                          </button>
                          {showChildDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden">
                              {[0, 1, 2, 3].map(num => (
                                <div
                                  key={num}
                                  onClick={() => { setEditData({...editData, child_seats: num}); setShowChildDropdown(false); }}
                                  className={`px-4 py-3 cursor-pointer transition-colors ${(editData.child_seats ?? booking?.child_seats ?? 0) === num ? 'bg-[#36827f]/10 text-[#36827f] font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                >
                                  {num}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="font-medium">
                        {(booking?.baby_seats > 0 || booking?.child_seats > 0) ? (
                          <>
                            {booking?.baby_seats > 0 && `${booking.baby_seats} baby`}
                            {booking?.baby_seats > 0 && booking?.child_seats > 0 && ', '}
                            {booking?.child_seats > 0 && `${booking.child_seats} child`}
                          </>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                    <Users className="h-4 w-4 text-[#36827f]" />
                  </div>
                  Contact Information
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div data-field="name">
                    <p className="text-sm text-gray-500 mb-1">Name</p>
                    {editMode ? (
                      <>
                        <input
                          value={editData.name ?? booking?.name ?? ''}
                          onChange={(e) => { setEditData({...editData, name: e.target.value}); setEditErrors(prev => ({...prev, name: null})); }}
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 hover:border-[#36827f] transition ${showEditErrors && editErrors.name ? 'border-red-500' : 'border-gray-200'}`}
                        />
                        {showEditErrors && editErrors.name && (
                          <p className="text-red-500 text-sm mt-1">{editErrors.name}</p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium">{booking?.name}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Email</p>
                    <p className="font-medium">{booking?.email}</p>
                  </div>
                  <div data-field="phone">
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                      <Phone className="h-4 w-4" /> Phone
                    </p>
                    {editMode ? (
                      <>
                        <input
                          value={editData.phone ?? booking?.phone ?? ''}
                          onChange={(e) => { setEditData({...editData, phone: e.target.value}); setEditErrors(prev => ({...prev, phone: null})); }}
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 hover:border-[#36827f] transition ${showEditErrors && editErrors.phone ? 'border-red-500' : 'border-gray-200'}`}
                        />
                        {showEditErrors && editErrors.phone && (
                          <p className="text-red-500 text-sm mt-1">{editErrors.phone}</p>
                        )}
                      </>
                    ) : (
                      <p className="font-medium">{booking?.phone}</p>
                    )}
                  </div>
                </div>
                
                {(booking?.special_requests || editMode) && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                      <MessageSquare className="h-4 w-4" /> Special Requests
                    </p>
                    {editMode ? (
                      <textarea
                        value={editData.special_requests ?? booking?.special_requests ?? ''}
                        onChange={(e) => setEditData({...editData, special_requests: e.target.value})}
                        placeholder="Any special requirements..."
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#36827f] focus:ring-2 focus:ring-[#36827f]/20 hover:border-[#36827f] transition resize-none"
                        rows={3}
                      />
                    ) : (
                      <p className="font-medium mt-1">{booking?.special_requests || 'None'}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Edit Mode Buttons */}
              {editMode && (
                <div className="flex gap-3">
                  <Button
                    onClick={handleEdit}
                    disabled={editLoading}
                    className="bg-[#36827f] hover:bg-[#2d6f6c]"
                  >
                    {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => { setEditMode(false); setEditData({}); setEditError(null); setEditErrors({}); setShowEditErrors(false); }}
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-700"
                  >
                    Cancel
                  </Button>
                </div>
              )}
              
              {editError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{editError}</p>
                </div>
              )}
            </div>

            {/* Sidebar - Actions & Payment */}
            <div className="space-y-6">
              {/* Price Summary */}
              <div className="bg-[#36827f]/5 rounded-2xl border border-[#36827f]/20 p-6">
                <h2 className="font-bold text-gray-900 mb-4">Price Summary</h2>
                <div className="space-y-2">
                  {/* Show breakdown for return trips */}
                  {booking?.has_return_trip ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Outbound Transfer</span>
                        <span>€{booking?.one_way_price || Math.round(booking?.total_price / 2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Return Transfer</span>
                        <span>+€{booking?.return_price || Math.round(booking?.total_price / 2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-[#36827f]/20">
                        <span className="text-gray-600">Total Price</span>
                        <span className="font-bold">€{booking?.total_price}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Price</span>
                      <span className="font-bold">€{booking?.total_price}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="font-bold text-green-600">€{booking?.amount_paid || 0}</span>
                  </div>
                  {remainingBalance > 0 && (
                    <div className="flex justify-between pt-2 border-t border-[#36827f]/20">
                      <span className="font-bold text-gray-900">Remaining</span>
                      <span className="text-xl font-bold text-[#36827f]">€{remainingBalance}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment */}
              {needsPayment && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                  <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-[#36827f]" />
                    </div>
                    Payment
                  </h2>
                  
                  {booking?.payment_status === 'unpaid' && (
                    <div className="space-y-3">
                      <Button
                        onClick={() => handlePayment('deposit')}
                        disabled={paymentProcessing}
                        className="w-full bg-[#36827f] hover:bg-[#2d6f6c]"
                      >
                        {paymentProcessing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Pay Deposit (€{Math.round(booking.total_price * 0.25)})
                      </Button>
                      <Button
                        onClick={() => handlePayment('full')}
                        disabled={paymentProcessing}
                        variant="outline"
                        className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-700"
                      >
                        Pay Full Amount (€{booking?.total_price})
                      </Button>
                    </div>
                  )}
                  
                  {booking?.payment_status === 'partial' && (
                    <div className="space-y-3">
                      <Button
                        onClick={() => handlePayment('remaining')}
                        disabled={paymentProcessing}
                        className="w-full bg-[#36827f] hover:bg-[#2d6f6c]"
                      >
                        {paymentProcessing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                        Pay Remaining (€{remainingBalance})
                      </Button>
                      <p className="text-sm text-gray-500 text-center">
                        Or pay the driver at pickup
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                <h2 className="font-bold text-gray-900 mb-4">Actions</h2>
                
                {canModify ? (
                  <div className="space-y-3">
                    <Button
                      onClick={() => setEditMode(true)}
                      variant="outline"
                      className="w-full justify-start border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-700"
                      disabled={editMode}
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit Reservation
                    </Button>
                    
                    <Button
                      onClick={() => setShowCancelConfirm(true)}
                      variant="outline"
                      className="w-full justify-start border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Cancel Reservation
                    </Button>
                  </div>
                ) : booking?.booking_status === 'cancelled' ? (
                  <p className="text-gray-500 text-sm">This booking has been cancelled.</p>
                ) : (
                  <div className="bg-amber-50 rounded-lg p-4">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                      <p className="text-amber-800 text-sm">
                        Changes cannot be made within 24 hours of pickup. Please <Link to="/contact" className="underline font-medium text-[#36827f]">contact us</Link> for assistance.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment History */}
              {transactions.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                  <h2 className="font-bold text-gray-900 mb-4">Payment History</h2>
                  <TransactionHistory 
                    transactions={transactions} 
                    isAdmin={false}
                    emptyMessage="No payments yet"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Cancel Confirmation Modal */}
          {showCancelConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Cancel Reservation?</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to cancel this booking? 
                  {(booking?.payment_status === 'paid' || booking?.payment_status === 'partial') && 
                    ' A full refund will be processed.'}
                </p>
                
                <div className="flex gap-3">
                  <Button
                    onClick={handleCancel}
                    disabled={cancelLoading}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    {cancelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Yes, Cancel Booking
                  </Button>
                  <Button
                    onClick={() => setShowCancelConfirm(false)}
                    variant="outline"
                  >
                    Keep Booking
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BookingManagementPage;
